// API bindings: chat (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { ChatInformation, ChatMessage, UserTarget, ModchatConfig, SlowchatConfig } from "./definitions";

export class ApiChat {
    /**
     * Method: GET
     * Path: /room/{roomId}/chat
     * Get chat information
     * @param roomId Room ID
     * @param headerParams Header parameters
     * @returns The request parameters
     */
    public static GetRoomRoomidChat(roomId: string, headerParams: GetRoomRoomidChatHeaderParameters): RequestParams<ChatInformation, GetRoomRoomidChatErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat`),
            headers: headerParams as any,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /room/{roomId}/chat/messages
     * Get chat messages (paginated, newest first).
     * Real time messages are received via websocket.
     * @param roomId Room ID
     * @param queryParams Query parameters
     * @param headerParams Header parameters
     * @returns The request parameters
     */
    public static GetRoomRoomidChatMessages(roomId: string, queryParams: GetRoomRoomidChatMessagesQueryParameters, headerParams?: GetRoomRoomidChatMessagesHeaderParameters): RequestParams<ChatMessage[], GetRoomRoomidChatMessagesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/messages` + generateURIQuery(queryParams)),
            headers: headerParams as any,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /room/{roomId}/chat/mute
     * Mutes user from chat
     * @param roomId Room ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static PostRoomRoomidChatMute(roomId: string, body: UserTarget): RequestParams<void, PostRoomRoomidChatMuteErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/mute`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "ALREADY", handler.badRequestAlready)
                    .add(400, "INVALID_USER", handler.badRequestInvalidUser)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /room/{roomId}/chat/unmute
     * Unmutes user from chat
     * @param roomId Room ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static PostRoomRoomidChatUnmute(roomId: string, body: UserTarget): RequestParams<void, PostRoomRoomidChatUnmuteErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/unmute`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "ALREADY", handler.badRequestAlready)
                    .add(400, "INVALID_USER", handler.badRequestInvalidUser)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /room/{roomId}/chat/modchat
     * Configures moderated chat
     * @param roomId Room ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static PostRoomRoomidChatModchat(roomId: string, body: ModchatConfig): RequestParams<void, PostRoomRoomidChatModchatErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/modchat`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /room/{roomId}/chat/slowchat
     * Configures slow chat
     * @param roomId Room ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static PostRoomRoomidChatSlowchat(roomId: string, body: SlowchatConfig): RequestParams<void, PostRoomRoomidChatSlowchatErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/slowchat`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /room/{roomId}/chat/message/{msgId}
     * Deletes message
     * @param roomId Room ID
     * @param msgId Message ID
     * @returns The request parameters
     */
    public static DeleteRoomRoomidChatMessageMsgid(roomId: string, msgId: string): RequestParams<void, DeleteRoomRoomidChatMessageMsgidErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/room/${encodeURIComponent(roomId)}/chat/message/${encodeURIComponent(msgId)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "MSG_NOT_FOUND", handler.notFoundMsgNotFound)
                    .add(404, "ROOM_NOT_FOUND", handler.notFoundRoomNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for GetRoomRoomidChat
 */
export type GetRoomRoomidChatErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Header parameters for GetRoomRoomidChat
 */
export interface GetRoomRoomidChatHeaderParameters {
    /**
     * Version ID (when joining with code)
     */
    "x-room-version-id"?: string;

    /**
     * Version Code (when joining with code)
     */
    "x-room-version-code"?: string;
}

/**
 * Error handler for GetRoomRoomidChatMessages
 */
export type GetRoomRoomidChatMessagesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Query parameters for GetRoomRoomidChatMessages
 */
export interface GetRoomRoomidChatMessagesQueryParameters {
    /**
     * Load messages older than this timestamp
     */
    before?: number;

    /**
     * Page size (default 50, max 100)
     */
    limit?: number;
}

/**
 * Header parameters for GetRoomRoomidChatMessages
 */
export interface GetRoomRoomidChatMessagesHeaderParameters {
    /**
     * Version ID (when joining with code)
     */
    "x-room-version-id"?: string;

    /**
     * Version Code (when joining with code)
     */
    "x-room-version-code"?: string;
}

/**
 * Error handler for PostRoomRoomidChatMute
 */
export type PostRoomRoomidChatMuteErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_USER
     */
    badRequestInvalidUser: () => void;

    /**
     * Handler for status = 400 and code = ALREADY
     */
    badRequestAlready: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for PostRoomRoomidChatUnmute
 */
export type PostRoomRoomidChatUnmuteErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_USER
     */
    badRequestInvalidUser: () => void;

    /**
     * Handler for status = 400 and code = ALREADY
     */
    badRequestAlready: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for PostRoomRoomidChatModchat
 */
export type PostRoomRoomidChatModchatErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for PostRoomRoomidChatSlowchat
 */
export type PostRoomRoomidChatSlowchatErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteRoomRoomidChatMessageMsgid
 */
export type DeleteRoomRoomidChatMessageMsgidErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Handler for status = 404 and code = ROOM_NOT_FOUND
     */
    notFoundRoomNotFound: () => void;

    /**
     * Handler for status = 404 and code = MSG_NOT_FOUND
     */
    notFoundMsgNotFound: () => void;
};

